package msw

import kotlinx.browser.window
import kotlin.js.json

private fun isDevelopmentHost(): Boolean {
    val hostname = window.location.hostname
    return hostname == "localhost" || hostname == "127.0.0.1" || hostname == "::1"
}

/** 開発環境ホストかつ URLパラメータ ?msw=true の場合のみ MSW を有効化 */
fun isMswEnabled(): Boolean {
    if (!isDevelopmentHost()) return false
    val searchParams = js("new URLSearchParams(window.location.search)")
    return (searchParams.get("msw") as? String) == "true"
}

/**
 * 一覧クエリのレスポンス用に要約フィールドを取り除いたコピーを返す。
 * Apollo は選択セットに無いフィールドが混ざるとパースに失敗するため、
 * 実サーバ同様「要求されたフィールドだけ」を返す必要がある。
 */
private fun withoutSummaryFields(item: dynamic): dynamic {
    val copy = js("Object.assign({}, item)")
    js("delete copy.summaryJa")
    js("delete copy.summaryStatus")
    js("delete copy.summarizedAt")
    return copy
}

/**
 * MSW (Mock Service Worker) を起動する。
 * 開発環境ホストで ?msw=true の場合のみ起動し、Service Worker の準備完了を待つ Promise を返す。
 * 無効時や初期化失敗時は null を返す。
 */
fun setupMsw(): dynamic {
    if (!isMswEnabled()) return null

    console.log("[MSW] Mock Service Worker を起動中...")

    // Kotlin 側で定義したモックデータを window スコープに公開
    MockData.publishToWindow()

    // MSW モジュールを require() で読み込み（各 js() は単一式）
    // js() は try/catch 内で使えないため（IR lowering 制約）、先に読み込む
    val msw: dynamic = js("require('msw')")
    val mswBrowser: dynamic = js("require('msw/browser')")
    val mockData: dynamic = js("window.__mswMockData")

    return try {
        // GraphQL ハンドラ: POST /graphql（相対パスで全オリジンにマッチ）
        val graphqlHandler = msw.http.post("/graphql", fun(info: dynamic): dynamic {
            // request.json() は Promise を返す → .then() で処理
            return info.request.json().then(fun(body: dynamic): dynamic {
                val query = (body.query as? String) ?: ""

                if (query.contains("GetTradeNewsItem")) {
                    val id = body.variables?.id
                    val found = mockData.find(fun(item: dynamic): Boolean {
                        return item.id == id
                    })
                    // 未ヒット時は undefined になるため明示的に null へ変換
                    return@then msw.HttpResponse.json(json(
                        "data" to json("tradeNewsItem" to (found ?: null))
                    ))
                }

                if (query.contains("GetTradeNewsByCategory")) {
                    val category = body.variables?.category
                    val filtered = mockData.filter(fun(item: dynamic): Boolean {
                        return item.category == category
                    }).map(fun(item: dynamic): dynamic { return withoutSummaryFields(item) })
                    return@then msw.HttpResponse.json(json(
                        "data" to json("tradeNewsByCategory" to filtered)
                    ))
                }

                val listData = mockData.map(fun(item: dynamic): dynamic {
                    return withoutSummaryFields(item)
                })
                return@then msw.HttpResponse.json(json(
                    "data" to json("tradeNews" to listData)
                ))
            })
        })

        // Health ハンドラ: GET /health
        val healthHandler = msw.http.get("/health", fun(_: dynamic): dynamic {
            return msw.HttpResponse.json(json("status" to "healthy"))
        })

        // Service Worker を起動
        val worker = mswBrowser.setupWorker(graphqlHandler, healthHandler)
        worker.start(json("onUnhandledRequest" to "bypass"))
    } catch (error: dynamic) {
        console.error("[MSW] Mock Service Worker の初期化に失敗しました。MSW なしで続行します。", error)
        null
    }
}
